import AssessmentIcon from "@mui/icons-material/Assessment";
import CalculateIcon from "@mui/icons-material/Calculate";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  alpha,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import ReactSpeedometer from "react-d3-speedometer";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../components/common/Util/Util";
import { ELIGIBILITY_CALCULATOR_ENDPOINTS } from "../../constants/apiEndpoints";
import MobileVerificationModal from "./MobileVerificationModal";
import ApplyLoanNowModal from "./ApplyLoanNowModal";
import { createLead, getLoanRequirements } from "../../services/leadService";
import { notify } from "../../services/advisorLeadService";
import { useDispatch } from "react-redux";
import { setLoanApplicationData } from "../../Store/userSlice";
import { BORROWER_TYPES, ENTITY_TYPES } from "../../utils/constants";
import { eligibilityCalculatorColumns } from "../../constants/tableColumns";

const EligibilityCalculatorModal = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [businessAge, setBusinessAge] = useState(0);
  const [entityType, setEntityType] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [totalSales, setTotalSales] = useState(1);
  const [fundsUsed, setFundsUsed] = useState("");
  const [emiHistory, setEmiHistory] = useState("");
  const [annualProfit, setAnnualProfit] = useState(5);
  const [loanRequirementData, setLoanRequirementData] = useState([]);
  const [eligibilityResponse, setEligibilityResponse] = useState(null);
  const [leadProcessed, setLeadProcessed] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [showMobileVerification, setShowMobileVerification] = useState(true);
  const [verifiedMobileNumber, setVerifiedMobileNumber] = useState(null);
  const [entityName, setEntityName] = useState("");
  const [loanAmount, setLoanAmount] = useState(10000);
  const [interestRate, setInterestRate] = useState(8.5);
  const [loanTenure, setLoanTenure] = useState(5);
  const [emiAmount, setEmiAmount] = useState(0);
  const [totalInterestAmount, setTotalInterestAmount] = useState(0);
  const [totalRepaymentAmount, setTotalRepaymentAmount] = useState(0);
  const [totalSalesInput, setTotalSalesInput] = useState("");
  const [annualProfitInput, setAnnualProfitInput] = useState("");
  const [showApplyLoanModal, setShowApplyLoanModal] = useState(false);
  const [submittedSnapshot, setSubmittedSnapshot] = useState(null);

  const elicalLabels = Object.fromEntries(
    (eligibilityCalculatorColumns?.columns || []).map((c) => [c.id, c.label])
  );
  const getColumnLabel = (id) => elicalLabels[id] || id;

  const resetAllFields = useCallback(() => {
    // Reset all form fields in a single batch
    const resetState = {
      businessAge: 0,
      entityType: "",
      businessType: "",
      totalSales: 1,
      fundsUsed: "",
      emiHistory: "",
      annualProfit: 5,
      eligibilityResponse: null,
      leadProcessed: false,
      tabIndex: 0,
      showMobileVerification: true,
      verifiedMobileNumber: null,
      entityName: "",
      loanAmount: 10000,
      interestRate: 8.5,
      loanTenure: 5,
      emiAmount: 0,
      totalInterestAmount: 0,
      totalRepaymentAmount: 0,
    };

    // Apply all resets synchronously
    Object.entries(resetState).forEach(([key, value]) => {
      const setterName = "set" + key.charAt(0).toUpperCase() + key.slice(1);
      if (typeof eval(setterName) === "function") {
        eval(setterName)(value);
      }
    });
  }, []);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      resetAllFields();
    }
  }, [open, resetAllFields]);

  const handleClose = useCallback(() => {
    if (leadProcessed && onSuccess) {
      onSuccess();
    }
    resetAllFields();
    onClose();
  }, [resetAllFields, onClose, leadProcessed, onSuccess]);

  const handleMobileVerificationSuccess = useCallback((mobileNumber) => {
    setShowMobileVerification(false);
    setVerifiedMobileNumber(mobileNumber);
  }, []);

  useEffect(() => {
    if (!submittedSnapshot) return; 
    const currentSnapshot = JSON.stringify({
      entityType,
      businessType,
      businessAge,
      totalSales,
      annualProfit,
      fundsUsed,
      emiHistory,
      entityName,
    });
    if (currentSnapshot !== submittedSnapshot) {
      if (leadProcessed) setLeadProcessed(false);
    } else {
      if (!leadProcessed) setLeadProcessed(true);
    }
  }, [entityType, businessType, businessAge, totalSales, annualProfit, fundsUsed, emiHistory, entityName, submittedSnapshot, leadProcessed]);

  useEffect(() => {
    if (open) {
      resetAllFields();
      fetchLoanRequirements();

      // Get user's IP
      fetch("https://api.ipify.org?format=json")
        .then((response) => response.json())
        .then((data) => {
          localStorage.setItem("userIp", data.ip);
        })
        .catch((error) => console.error("Error fetching IP:", error));

      // Get user's coordinates
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coordinates = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            localStorage.setItem(
              "userCoordinates",
              JSON.stringify(coordinates)
            );
          },
          (error) => console.error("Error getting location:", error),
          { enableHighAccuracy: true }
        );
      }
    }
  }, [open]);

  // EMI Calculator useEffect
  useEffect(() => {
    const principal = loanAmount;
    const rate = interestRate / 100 / 12;
    const tenure = loanTenure * 12;
    setTimeout(() => {
      if (principal && rate && tenure) {
        const emi =
          (principal * rate * Math.pow(1 + rate, tenure)) /
          (Math.pow(1 + rate, tenure) - 1);
        setEmiAmount(emi?.toFixed(2));
        setTotalInterestAmount((emi * tenure - principal).toFixed(2));
        setTotalRepaymentAmount((emi * tenure).toFixed(2));
      }
    }, 200);
  }, [loanAmount, interestRate, loanTenure]);

  const fetchLoanRequirements = async () => {
    try {
      getLoanRequirements().then((data)=>{
        if (data?.statusCode === 200) {
          setLoanRequirementData(data?.result);
        } else if (data?.statusCode === 401) {
          // Token might be expired, clear it
          localStorage.removeItem("webToken");
          showToast("Session expired. Please try again.", "error");
        }
      }).catch((error)=>{
        console.error("Error fetching loan requirements:", error);
        showToast("Failed to fetch loan requirements", "error");
      })
    } catch (error) {
      console.error("Error fetching loan requirements:", error);
      showToast("Failed to fetch loan requirements", "error");
    }
  };

  const handleCheckEligibility = async () => {
    
    if (loading || leadProcessed) {
      return;
    }
    if (
      !entityType ||
      !fundsUsed ||
      !emiHistory ||
      !businessType ||
      !entityName
    ) {
      showToast("Please select all required fields", "error");
      return;
    }

    setLoading(true);
    try {
      // Get IP and coordinates from localStorage or default to empty strings
      const storedIp = localStorage.getItem("userIp") || "";
      const storedCoordinates = JSON.parse(
        localStorage.getItem("userCoordinates") || "{}"
      );

      const leadRequest = {
        leadType: "elical",
        mobileNumber: verifiedMobileNumber,
        entityName: entityName,
        ip: storedIp,
        longitude: String(storedCoordinates?.lng || ""),
        latitude: String(storedCoordinates?.lat || ""),
        source: "admin",
        eliCal: {
          entityType,
          businessType,
          businessAge,
          // Convert numbers to strings with 2 decimal places
          totalSales: totalSales.toFixed(2),
          annualProfit: annualProfit.toFixed(2),
          fundsUsed,
          emiHistory,
          entityName: entityName,
        },
      };

      // Lead API call without authentication headers
      createLead(leadRequest).then((response) => {
        if (response?.statusCode === 200) {
          // Store the token if provided
          showToast(response.message, "success");
          setEligibilityResponse(response.result);
          setLeadProcessed(true);
          setSubmittedSnapshot(
            JSON.stringify({
              entityType,
              businessType,
              businessAge,
              totalSales,
              annualProfit,
              fundsUsed,
              emiHistory,
              entityName,
            })
          );
          localStorage.setItem('leadsApiResponse', JSON.stringify(response));
          // Send notification with authentication
          const notifyReq = {
            title: "New Lead Generated",
            content: "A new loan eligibility lead has been generated.",
            type: "leadGeneration",
            action: "View Lead",
            actionUrl: "/leads",
            unread: true,
          }
          notify(notifyReq).catch((error) => {
            console.error("Error sending notification:", error);
            // Don't show toast for
          })
          setLoading(false);

        } else {
          showToast(
            response?.message || "Failed to process eligibility request",
            "error"
          );
          setLoading(false);
        }
      }).catch((error) => {
        console.error("Opps! somwthing went wrong", error);
        console.error("Error creating lead:", error);
        setLoading(false);
      })

    } catch (error) {
      console.error("Error in eligibility process:", error);
      showToast("Failed to process eligibility. Please try again.", "error");
      setLoading(false);
    }
  };

  const formatCurrency = (value, isEligibilityAmount = false) => {
    const amount = isEligibilityAmount ? value * 100000 : value;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getAmountInWords = (amount) => {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const convertLessThanOneThousand = (n) => {
      if (n === 0) return "";

      if (n < 10) return ones[n];

      if (n < 20) return teens[n - 10];

      if (n < 100) {
        return (
          tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "")
        );
      }

      return (
        ones[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 !== 0 ? " " + convertLessThanOneThousand(n % 100) : "")
      );
    };

    if (amount === 0) return "Zero";

    const lakhs = Math.floor(amount);
    let result = "";

    if (lakhs > 0) {
      result +=
        convertLessThanOneThousand(lakhs) + " Lakh" + (lakhs > 1 ? "s" : "");
    }

    return result.trim();
  };

  const numberFormatter = (amount) => {
    const loanAmount = Number(amount);
    return loanAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const formatAmountLabel = (value) => {
    if (value < 100) {
      return `${value.toFixed(2)} Lacs`;
    } else {
      return `${(value / 100).toFixed(2)} Cr`;
    }
  };

  const parseInputValue = (value) => {
    // Remove all non-numeric characters except decimal point
    const numStr = value.replace(/[^0-9.]/g, "");
    return parseFloat(numStr);
  };

  const handleloanAmount = (amount) => {
    const loanAmount = Number(amount) * 100000;
    return loanAmount.toLocaleString("en-IN");
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          background: "linear-gradient(to bottom, #ffffff, #fafafa)",
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AssessmentIcon sx={{ color: "#004AAD" }} />
          <Typography
            variant="h6"
            sx={{
              color: "#004AAD",
              fontWeight: 600,
              fontSize: "1.25rem",
              letterSpacing: "-0.01em",
            }}
          >
            Loan Eligibility Calculator
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Backdrop sx={{ color: "#fff", zIndex: 1000 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: alpha("#004AAD", 0.02),
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "0.95rem",
              fontWeight: 500,
              minHeight: "56px",
              "&.Mui-selected": {
                color: "#004AAD",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#004AAD",
            },
          }}
        >
          <Tab
            icon={<CalculateIcon />}
            iconPosition="start"
            label="Eligibility Calculator"
          />
          <Tab
            icon={<TrendingUpIcon />}
            iconPosition="start"
            label="EMI Calculator"
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {showMobileVerification ? (
            <MobileVerificationModal
              open={showMobileVerification}
              onClose={handleClose}
              onVerificationSuccess={handleMobileVerificationSuccess}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {tabIndex === 0 ? (
                // Eligibility Calculator Content
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Paper
                      sx={{
                        p: 4,
                        backgroundColor: alpha("#f8f9fa", 0.8),
                        borderRadius: 2,
                        height: "100%",
                      }}
                    >
                      {/* Entity Name */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {getColumnLabel("entityName")} {" "}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        </Typography>
                        <TextField
                          fullWidth
                          value={entityName}
                          onChange={(e) => setEntityName(e.target.value)}
                          placeholder="Enter Entity Name"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              "&.Mui-focused": {
                                boxShadow: "0 0 0 2px rgba(0, 74, 173, 0.2)",
                              },
                            },
                          }}
                        />
                      </Box>

                      {/* Nature of Entity */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {getColumnLabel("entityType")} {" "}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        </Typography>
                        <RadioGroup
                          row
                          value={entityType}
                          onChange={(e) => {setEntityType(e.target.value)}}
                          sx={{
                            gap: 2,
                            "& .MuiRadio-root": {
                              color: alpha("#004AAD", 0.6),
                              "&.Mui-checked": {
                                color: "#004AAD",
                              },
                            },
                          }}
                        >
                          {loanRequirementData?.entity_type?.map((item) => (
                            <FormControlLabel
                              key={item.value}
                              value={item.value}
                              control={<Radio />}
                              label={item.value}
                              sx={{
                                backgroundColor:
                                  entityType === item.value
                                    ? alpha("#004AAD", 0.04)
                                    : "transparent",
                                borderRadius: 1,
                                px: 1,
                                mx: 0,
                              }}
                            />
                          ))}
                        </RadioGroup>
                      </Box>

                      {/* Business Type */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {getColumnLabel("businessType")} {" "}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        </Typography>
                        <RadioGroup
                          row
                          value={businessType}
                          onChange={(e) => setBusinessType(e.target.value)}
                          sx={{
                            gap: 2,
                            "& .MuiRadio-root": {
                              color: alpha("#004AAD", 0.6),
                              "&.Mui-checked": {
                                color: "#004AAD",
                              },
                            },
                          }}
                        >
                          {loanRequirementData?.business_type?.map((item) => (
                            <FormControlLabel
                              key={item.value}
                              value={item.value}
                              control={<Radio />}
                              label={item.value}
                              sx={{
                                backgroundColor:
                                  businessType === item.value
                                    ? alpha("#004AAD", 0.04)
                                    : "transparent",
                                borderRadius: 1,
                                px: 1,
                                mx: 0,
                              }}
                            />
                          ))}
                        </RadioGroup>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          style={{ marginTop: "24px" }}
                        >
                          {getColumnLabel("businessAge")} {" "}
                          <Typography component="span" sx={{ color: "red" }}>
                            *
                          </Typography>
                        </Typography>
                        {/* <Typography
                    variant="subtitle1"
                    gutterBottom
                    style={{ marginTop: "24px" }}
                  >
                    {businessAge === 10 ? "More than 10 Years" : `${businessAge} ${businessAge > 1 ? 'Years' : "Year"}`}
                  </Typography> */}
                        <FormControl
                          sx={{ m: 1, width: "21ch", height: "50px" }}
                          variant="outlined"
                        >
                          <OutlinedInput
                            id="outlined-adornment-weight"
                            // disabled
                            value={businessAge}
                            onChange={(e) => {
                              let value = e.target.value.replace(/,/g, "");
                              const newValue = Number(value);
                              if (!isNaN(newValue) && newValue <= 30) {
                                // setLoanTenure(newValue); // update value without commas
                                setBusinessAge(newValue);
                              }
                            }}
                            inputProps={{
                              maxLength: 2, // just extra safety for browser
                            }}
                            placeholder="E.g., 3 year"
                            startAdornment={
                              <InputAdornment position="start">
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Box sx={{ pr: 1, fontWeight: 600 }}>
                                    Yrs.
                                  </Box>
                                  <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                      height: "40px",
                                      bgcolor: "5A5A5A",
                                      color: "#5A5A5A",
                                    }}
                                  />
                                </Box>
                              </InputAdornment>
                            }
                          />
                        </FormControl>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Slider
                          value={businessAge}
                          min={0}
                          max={10}
                          step={0.5}
                          onChange={(e, newValue) => setBusinessAge(newValue)}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${value} Years`}
                          sx={{
                            marginRight: "16px",
                            color: "#BDBDBD",
                            "& .MuiSlider-thumb": {
                              backgroundColor: "#18CB96",
                            },
                            "& .MuiSlider-track": {
                              backgroundColor: "#18CB96",
                            },
                            "& .MuiSlider-rail": {
                              backgroundColor: "#E8E8E8",
                            },
                            "&:active .MuiSlider-track": {
                              backgroundColor: "#18CB96",
                            },
                            height: "8px",
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          style={{ marginTop: "24px" }}
                        >
                          {getColumnLabel("totalSales")} {" "}
                          <Typography component="span" sx={{ color: "red" }}>
                            *
                          </Typography>
                        </Typography>
                        <FormControl
                          sx={{ m: 1, width: "21ch", height: "50px" }}
                          variant="outlined"
                        >
                          <OutlinedInput
                            id="outlined-adornment-weight"
                            inputProps={{
                              maxLength: 12, // just extra safety for browser
                            }}
                            value={
                              totalSales === 0
                                ? ""
                                : new Intl.NumberFormat("en-IN").format(
                                  totalSales.toFixed(0)
                                )
                            }
                            onChange={(e) => {
                              let value = e.target.value.replace(/,/g, "");
                              const newValue = Number(value);
                              if (!isNaN(newValue)) {
                                setTotalSales(newValue); // update value without commas
                              }
                            }}
                            placeholder="E.g., 5,00,000"
                            startAdornment={
                              <InputAdornment position="start">
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Box sx={{ pr: 1, fontWeight: 600 }}>INR</Box>
                                  <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                      height: "40px",
                                      bgcolor: "5A5A5A",
                                      color: "#5A5A5A",
                                    }}
                                  />
                                </Box>
                              </InputAdornment>
                            }
                          />
                        </FormControl>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Slider
                          value={totalSales.toFixed(2)}
                          min={0}
                          max={400000000}
                          step={0.0001}
                          onChange={(e, newValue) => setTotalSales(newValue)}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) =>
                            value / 100000 < 100
                              ? `${(value / 100000).toFixed(2)} Lacs`
                              : value / 100000 <= 4000
                                ? `${(value / 10000000).toFixed(2)} Cr`
                                : "More than 40 Cr"
                          }
                          sx={{
                            marginRight: "16px",
                            color: "#BDBDBD",
                            "& .MuiSlider-thumb": {
                              backgroundColor: "#18CB96",
                            },
                            "& .MuiSlider-track": {
                              backgroundColor: "#18CB96",
                            },
                            "& .MuiSlider-rail": {
                              backgroundColor: "#E8E8E8",
                            },
                            "&:active .MuiSlider-track": {
                              backgroundColor: "#18CB96",
                            },
                            height: "8px",
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          style={{ marginTop: "24px" }}
                        >
                          {getColumnLabel("annualProfit")} {" "}
                          <Typography component="span" sx={{ color: "red" }}>
                            *
                          </Typography>
                        </Typography>
                        <FormControl
                          sx={{ m: 1, width: "21ch", height: "50px" }}
                          variant="outlined"
                        >
                          <OutlinedInput
                            id="outlined-adornment-weight"
                            inputProps={{
                              maxLength: 12, // just extra safety for browser
                            }}
                            value={
                              annualProfit === 0
                                ? ""
                                : new Intl.NumberFormat("en-IN").format(
                                  annualProfit.toFixed(0)
                                )
                            }
                            onChange={(e) => {
                              let value = e.target.value.replace(/,/g, "");
                              const newValue = Number(value);
                              if (!isNaN(newValue)) {
                                setAnnualProfit(newValue); // update value without commas
                              }
                            }}
                            placeholder="E.g., 5,00,000"
                            startAdornment={
                              <InputAdornment position="start">
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Box sx={{ pr: 1, fontWeight: 600 }}>INR</Box>
                                  <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                      height: "40px",
                                      bgcolor: "5A5A5A",
                                      color: "#5A5A5A",
                                    }}
                                  />
                                </Box>
                              </InputAdornment>
                            }
                          />
                        </FormControl>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Slider
                          value={annualProfit}
                          min={0}
                          max={20000000}
                          step={0.001}
                          onChange={(e, newValue) => setAnnualProfit(newValue)}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) =>
                            value / 100000 < 100
                              ? `${(value / 100000).toFixed(2)} Lacs`
                              : value / 100000 <= 200
                                ? `${(value / 10000000).toFixed(2)} Cr`
                                : "More than 2 Cr"
                          }
                          sx={{
                            marginRight: "16px",
                            color: "#BDBDBD",
                            "& .MuiSlider-thumb": {
                              backgroundColor: "#18CB96",
                            },
                            "& .MuiSlider-track": {
                              backgroundColor: "#18CB96",
                            },
                            "& .MuiSlider-rail": {
                              backgroundColor: "#E8E8E8",
                            },
                            "&:active .MuiSlider-track": {
                              backgroundColor: "#18CB96",
                            },
                            height: "8px",
                          }}
                        />
                      </Box>

                      {/* Funds Used */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {getColumnLabel("fundsUsed")} {" "}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        </Typography>
                        <RadioGroup
                          row
                          value={fundsUsed}
                          onChange={(e) => setFundsUsed(e.target.value)}
                          sx={{
                            gap: 2,
                            "& .MuiRadio-root": {
                              color: alpha("#004AAD", 0.6),
                              "&.Mui-checked": {
                                color: "#004AAD",
                              },
                            },
                          }}
                        >
                          {loanRequirementData?.funds_used?.map((item) => (
                            <FormControlLabel
                              key={item.value}
                              value={item.value}
                              control={<Radio />}
                              label={item.value}
                              sx={{
                                backgroundColor:
                                  fundsUsed === item.value
                                    ? alpha("#004AAD", 0.04)
                                    : "transparent",
                                borderRadius: 1,
                                px: 1,
                                mx: 0,
                              }}
                            />
                          ))}
                        </RadioGroup>
                      </Box>

                      {/* EMI History */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {getColumnLabel("emiHistory")} {" "}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        </Typography>
                        <RadioGroup
                          row
                          value={emiHistory}
                          onChange={(e) => setEmiHistory(e.target.value)}
                          sx={{
                            gap: 2,
                            "& .MuiRadio-root": {
                              color: alpha("#004AAD", 0.6),
                              "&.Mui-checked": {
                                color: "#004AAD",
                              },
                            },
                          }}
                        >
                          {loanRequirementData?.emi_history?.map((item) => (
                            <FormControlLabel
                              key={item.value}
                              value={item.value}
                              control={<Radio />}
                              label={item.value}
                              sx={{
                                backgroundColor:
                                  emiHistory === item.value
                                    ? alpha("#004AAD", 0.04)
                                    : "transparent",
                                borderRadius: 1,
                                px: 1,
                                mx: 0,
                              }}
                            />
                          ))}
                        </RadioGroup>
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleCheckEligibility}
                        disabled={loading || leadProcessed}
                        sx={{
                          mt: 2,
                          backgroundColor: "#004AAD",
                          borderRadius: 1.5,
                          py: 1.5,
                          textTransform: "none",
                          fontSize: "1rem",
                          fontWeight: 500,
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#003c8f",
                            boxShadow: "0 4px 12px rgba(0, 74, 173, 0.2)",
                          },
                        }}
                      >
                        {loading ? "Processing..." : leadProcessed ? "Processed" : "Check Eligibility"}
                      </Button>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Paper
                        style={{ padding: "14px", backgroundColor: "#EDF0FF" }}
                      >
                        <Typography
                          variant="subtitle1"
                          color="textSecondary"
                          gutterBottom
                          sx={{ color: "#333333", fontWeight: 600 }}
                        >
                          Your Loan Approval Probability
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          color="textSecondary"
                          gutterBottom
                          sx={{
                            color: "#5A5A5A",
                            fontStyle: "italic",
                          }}
                        >
                          Based on your financial profile, here's your estimated
                          chance of getting an instant loan approval.
                        </Typography>
                        {/* Speedometer Section */}
                        <Box sx={{ textAlign: "center" }}>
                          <ReactSpeedometer
                            minValue={0}
                            maxValue={97}
                            height={175}
                            width={280}
                            // currentValueText=""
                            value={Math.max(
                              eligibilityResponse?.instantApproval?.score || 0,
                              0
                            )} // Stops at 0 if negative
                            currentValueText={`Score: ${
                              eligibilityResponse?.instantApproval?.score || 0
                              }`} // Shows real score, even if negative
                            needleTransition="easeQuadIn"
                            needleTransitionDuration={1000}
                            needleColor="black"
                            needleHeightRatio={0.4}
                            customSegmentStops={[0, 35, 65, 97]} // Define exact segment stops
                            segmentColors={["#FF4942", "#FFC916", "#A4CD3C"]} // Colors match the segments
                            ringWidth={50}
                            maxSegmentLabels={0}
                            segmentBorderColor="black"
                            segmentBorderWidth={15}
                          />
                        </Box>
                        <Typography
                          variant="body1"
                          align="center"
                          color="#004AAD"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          {!leadProcessed ? (
                            "Fill in all details and check eligibility to see your approval chances"
                          ) : eligibilityResponse?.instantApproval?.score <
                            35 ? (
                            <>
                              <Box
                                sx={{
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: "#FF4942",
                                  borderRadius: "4px",
                                }}
                              ></Box>
                              We foresee some potential hurdles in the loan
                              approval.
                            </>
                          ) : eligibilityResponse?.instantApproval?.score <
                            66 ? (
                            <>
                              <Box
                                sx={{
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: "#FFC916",
                                  borderRadius: "4px",
                                }}
                              ></Box>
                              We see a fair opportunity for your loan to be
                              approved.
                            </>
                          ) : (
                            <>
                              <Box
                                sx={{
                                  width: "30px",
                                  height: "30px",
                                  backgroundColor: "#A4CD3C",
                                  borderRadius: "4px",
                                }}
                              ></Box>
                              You have a very strong chance of Instant approval.
                            </>
                          )}
                        </Typography>
                      </Paper>
                      <Paper
                        style={{ padding: "24px", backgroundColor: "#EDF0FF" }}
                      >
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          sx={{
                            color: "#333333",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          Suggested Loan Products
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, mb: 1 }}
                                >
                                  Loan Products Full Name
                                </Typography>
                                <Typography variant="body2">
                                  OD - Overdraft
                                </Typography>
                                <Typography variant="body2">
                                  CC - Cash Credit
                                </Typography>
                                <Typography variant="body2">
                                  LAP - Loan Against Property
                                </Typography>
                                <Typography variant="body2">
                                  DLOD - Dropline Overdraft
                                </Typography>
                                <Typography variant="body2">
                                  BL - Business Loan
                                </Typography>
                                <Typography variant="body2">
                                  ML - Machinery Loan
                                </Typography>
                                <Typography variant="body2">
                                  EL - Equipment Loan
                                </Typography>
                                <Typography variant="body2">
                                  SCF - Supply Chain Finance
                                </Typography>
                                <Typography variant="body2">
                                  CGTMSE - Credit Guarantee Fund Trust for Micro
                                  and Small Enterprises
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="right"
                          >
                            <IconButton size="small" sx={{ color: "#004AAD" }}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#5A5A5A",
                            fontStyle: "italic",
                            marginBottom: "20px",
                          }}
                        >
                          We have shortlisted the best loan products based on
                          your selected purpose and financial details.
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2}>
                          <Box display="flex" justifyContent={"space-between"}>
                            <Typography
                              sx={{
                                color: "#5A5A5A",
                                fontSize: "0.9rem",
                                paddingY: "6px",
                              }}
                            >
                              Secured Products
                            </Typography>
                            <Typography
                              sx={{
                                color: "#5A5A5A",
                                fontSize: "1rem",
                                backgroundColor: "#B3D1FF",
                                fontWeight: 600,
                                paddingX: "15px",
                                paddingY: "5px",
                                borderRadius: "5px",
                              }}
                            >
                              {
                                eligibilityResponse?.recommendedProducts
                                  ?.secured_products
                              }
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent={"space-between"}>
                            <Typography
                              sx={{
                                color: "#5A5A5A",
                                fontSize: "0.9rem",
                                paddingY: "6px",
                              }}
                            >
                              Unsecured Products
                            </Typography>
                            <Typography
                              sx={{
                                color: "#5A5A5A",
                                fontSize: "1rem",
                                backgroundColor: "#B3D1FF",
                                fontWeight: 600,
                                paddingX: "15px",
                                paddingY: "5px",
                                borderRadius: "5px",
                              }}
                            >
                              {
                                eligibilityResponse?.recommendedProducts
                                  ?.unsecured_products
                              }
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                      <Paper
                        style={{ padding: "24px", backgroundColor: "#EDF0FF" }}
                      >
                        <Typography
                          variant="subtitle1"
                          color="textSecondary"
                          gutterBottom
                          sx={{ color: "#333333", fontWeight: 600 }}
                        >
                          Estimated Loan Amount
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{
                            color: "#5A5A5A",
                            fontStyle: "italic",
                            marginBottom: "20px",
                          }}
                        >
                          Here's how much loan you may be eligible for based on
                          your business revenue and purpose.
                        </Typography>
                        <Typography
                          variant="h5"
                          align="left"
                          style={{
                            fontWeight: 700,
                            fontSize: "1.5rem",
                            color: "#FF9500",
                            marginTop: "15px",
                            marginBottom: "10px",
                          }}
                        >
                          ₹{" "}
                          {handleloanAmount(
                            eligibilityResponse?.eligibleAmount?.loanAmount || 0
                          )}
                        </Typography>
                        <Typography
                          variant="body1"
                          align="left"
                          style={{
                            color: "#5A5A5A",
                            fontWeight: 500,
                            marginBottom: "15px",
                          }}
                        >
                          {(eligibilityResponse?.eligibleAmount?.loanAmount ||
                            0) > 0 &&
                            `${getAmountInWords(
                              eligibilityResponse?.eligibleAmount?.loanAmount ||
                              0
                            )} (Amount in words)`}
                        </Typography>
                        {(eligibilityResponse?.eligibleAmount?.loanAmount ||
                          0) > 0 && (
                            <Typography
                              variant="body1"
                              align="left"
                              style={{
                                color: "#5A5A5A",
                                fontWeight: "bolder",
                                fontSize: 12,
                                marginBottom: "15px",
                              }}
                            >
                              *Assumption: Proposed Loan amount is long-term in
                              nature, repayable in{" "}
                              <span style={{ color: "#FF9500" }}>7 years.</span>
                            </Typography>
                          )}
                          
                          {leadProcessed && (
                            <Box sx={{ mt: 3 }}>
                              <Button
                                fullWidth
                                variant="contained"
                                onClick={() => setShowApplyLoanModal(true)}
                                sx={{
                                  backgroundColor: "#004AAD",
                                  borderRadius: 1.5,
                                  py: 1.5,
                                  textTransform: "none",
                                  fontSize: "1rem",
                                  fontWeight: 600,
                                  boxShadow: "0 4px 12px rgba(0, 74, 173, 0.3)",
                                  "&:hover": {
                                    backgroundColor: "#003c8f",
                                    boxShadow: "0 6px 16px rgba(0, 74, 173, 0.4)",
                                    transform: "translateY(-2px)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                              >
                                Apply Loan Now
                              </Button>
                            </Box>
                          )}
                      </Paper>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                // EMI Calculator Content
                <Grid container spacing={3}>
                  <Grid item xs={12} md={7}>
                    <Paper
                      sx={{
                        p: 4,
                        backgroundColor: alpha("#f8f9fa", 0.8),
                        borderRadius: 2,
                        height: "100%",
                      }}
                    >
                      {/* Loan Amount */}
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {getColumnLabel("loanAmount")} {" "}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 2, alignItems: "center" }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Slider
                              value={loanAmount}
                              onChange={(e, value) => setLoanAmount(value)}
                              min={10000}
                              max={10000000}
                              step={10000}
                              sx={{
                                color: "#004AAD",
                                "& .MuiSlider-thumb": {
                                  "&:hover, &.Mui-focusVisible": {
                                    boxShadow:
                                      "0 0 0 8px rgba(0, 74, 173, 0.16)",
                                  },
                                },
                              }}
                            />
                          </Box>
                          <TextField
                            value={formatCurrency(loanAmount)}
                            onChange={(e) => {
                              const value = parseInt(
                                e.target.value.replace(/[^0-9]/g, "")
                              );
                              if (!isNaN(value)) {
                                setLoanAmount(
                                  Math.min(10000000, Math.max(10000, value))
                                );
                              }
                            }}
                            InputProps={{
                              startAdornment: (
                                <Typography sx={{ mr: 1 }}>₹</Typography>
                              ),
                              sx: { width: "180px", borderRadius: 1.5 },
                            }}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {/* Interest Rate */}
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {getColumnLabel("interestRate")} {" "}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 2, alignItems: "center" }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Slider
                              value={interestRate}
                              onChange={(e, value) => setInterestRate(value)}
                              min={5}
                              max={15}
                              step={0.5}
                              sx={{
                                color: "#004AAD",
                                "& .MuiSlider-thumb": {
                                  "&:hover, &.Mui-focusVisible": {
                                    boxShadow:
                                      "0 0 0 8px rgba(0, 74, 173, 0.16)",
                                  },
                                },
                              }}
                            />
                          </Box>
                          <TextField
                            value={interestRate}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value)) {
                                setInterestRate(
                                  Math.min(15, Math.max(5, value))
                                );
                              }
                            }}
                            InputProps={{
                              endAdornment: (
                                <Typography sx={{ ml: 1 }}>%</Typography>
                              ),
                              sx: { width: "120px", borderRadius: 1.5 },
                            }}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {/* Loan Tenure */}
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {getColumnLabel("loanTenure")} {" "}
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        </Typography>
                        <Box
                          sx={{ display: "flex", gap: 2, alignItems: "center" }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Slider
                              value={loanTenure}
                              onChange={(e, value) => setLoanTenure(value)}
                              min={1}
                              max={30}
                              sx={{
                                color: "#004AAD",
                                "& .MuiSlider-thumb": {
                                  "&:hover, &.Mui-focusVisible": {
                                    boxShadow:
                                      "0 0 0 8px rgba(0, 74, 173, 0.16)",
                                  },
                                },
                              }}
                            />
                          </Box>
                          <TextField
                            value={loanTenure}
                            onChange={(e) => {
                              const value = parseInt(e.target.value);
                              if (!isNaN(value)) {
                                setLoanTenure(Math.min(30, Math.max(1, value)));
                              }
                            }}
                            InputProps={{
                              endAdornment: (
                                <Typography sx={{ ml: 1 }}>Years</Typography>
                              ),
                              sx: { width: "120px", borderRadius: 1.5 },
                            }}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      {/* Estimated EMI Card */}
                      <Paper
                        sx={{
                          p: 3,
                          backgroundColor: alpha("#004AAD", 0.04),
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "#333333",
                            fontWeight: 600,
                            mb: 2,
                          }}
                        >
                          Your Estimated EMI
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            color: "#FF9500",
                            fontWeight: 700,
                            mb: 2,
                          }}
                        >
                          ₹{numberFormatter(emiAmount)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#5A5A5A",
                            fontStyle: "italic",
                          }}
                        >
                          Based on your loan details, here's what you'll need to
                          pay each month.
                        </Typography>
                      </Paper>

                      {/* Loan Estimate Card */}
                      <Paper
                        sx={{
                          p: 3,
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: "#333333",
                            fontWeight: 600,
                            mb: 2,
                          }}
                        >
                          Your Loan Estimate is Ready!
                        </Typography>
                        <Typography
                          sx={{
                            color: "#5A5A5A",
                            fontStyle: "italic",
                            mb: 3,
                            fontSize: "0.875rem",
                          }}
                        >
                          Here's a breakdown of your estimated loan repayment
                          details. Review these figures to plan your finances
                          confidently.
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              p: 2,
                              backgroundColor: alpha("#f8f9fa", 0.8),
                              borderRadius: 1,
                            }}
                          >
                            <Typography sx={{ color: "#5A5A5A" }}>
                              Total Interest Payable
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              ₹{numberFormatter(totalInterestAmount)}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              p: 2,
                              backgroundColor: alpha("#f8f9fa", 0.8),
                              borderRadius: 1,
                            }}
                          >
                            <Typography sx={{ color: "#5A5A5A" }}>
                              Total Repayment Amount
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              ₹{numberFormatter(totalRepaymentAmount)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <ApplyLoanNowModal
        open={showApplyLoanModal}
        onClose={() => setShowApplyLoanModal(false)}
      />
    </Dialog>
  );
};

export default EligibilityCalculatorModal;
